import React, { FC, useMemo, useState } from 'react'
import { institutionsProgramIdSelectors } from '@ucheba/store/institutions/programs/programId'
import { useSelector } from 'react-redux'
import { Heading } from '@ucheba/ui/components/Heading/desktop'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'
import { feedbacksSelectors } from '@ucheba/store/feedbacks'
import { questionsSelectors } from '@ucheba/store/questions'
import { Text } from '@ucheba/ui/components/Text/desktop'
import { EProgramTypes } from '@ucheba/utils/types/program'
import { EInstitutionType } from '@ucheba/store/institutions/item/types'
import { analyticEvent } from '@ucheba/utils/helpers/analytics/events'
import {
  EAnalyticEvents,
  EAnalyticEventTargets,
  EAnalyticSections,
} from '@ucheba/utils/helpers/analytics/events/types'
import { useRouter } from 'next/router'
import { EActivityInfoPart, EActivityTypes } from '@ucheba/utils/types/userActivityTypes'
import { authSelectors } from '@ucheba/store/auth'
import ProgramPageAbout from './ProgramPageAbout'
import ProgramPageChips from './ProgramPageChips'
import PointsInfo from '../components/PointsInfo/desktop'
import StudyOptions from '../components/StudyOptions/desktop'
import Syllabus from '../components/Syllabus/desktop'
import AdmissionConditions from '../components/AdmissionConditions/desktop/AdmissionConditions'
import AdditionalPoints from '../components/AdditionalPoints/desktop'
import ForWhomProgram from '../components/ForWhomProgram/desktop'
import StudyProgram from '../components/StudyProgram/desktop'
import StudyAdvantages from '../components/StudyAdvantages/desktop'
import LearningOutcomes from '../components/LearningOutcomes/desktop'
import ProgramIdPageTeachers from './ProgramIdPageTeachers'
import ProgramIdPageEmployers from './ProgramIdPageEmployers'
import { isCoursesCheck } from '../bll/isCoursesCheck'
import ProgramPageQuestions from './ProgramPageQuestions'
import ProgramPageFeedbacks from './ProgramPageFeedbacks'
import ProgramPageSimilarPrograms from './ProgramPageSimilarPrograms'
import ProgramPageSimilarProgramsOthers from './ProgramPageSimilarProgramsOthers'
import InstitutionSeeAlso from '../../../../../../components/InstitutionSeeAlso/desktop'
import { hasBudgetPlaces } from '../bll/hasBudgetPlaces'
import { usePaidPackage } from '../bll/usePaidPackage'
import { RotationBannerAndAdmissionInstructions } from '../components/RotationBannerAndAdmissionInstructions/desktop'
import LidMagnetForm from '../../../../../../components/LidMagnetForm/desktop'
import {
  EApplicationFormProductTypes,
  EApplicationFunnels,
} from '../../../../../../components/AppliactionForm/types'
import { ETypeRequest } from '../../../../../for-abiturients/ege/index/types'
import ExtraInfo from '../components/ExtraInfo/desktop'
import { useUserActivity } from '../../../../../../../utils/hooks/useUserActivity'
import { useGetInstitutionsId } from '../bll/useGetInstitutionsId'
import ProgramIdPageProfiles from './ProgramIdPageProfiles'

const ProgramPageContent: FC = () => {
  const isAuth = useSelector(authSelectors.isAuth)
  const program = useSelector(institutionsProgramIdSelectors.entity)
  const feedbacks = useSelector(feedbacksSelectors.entity)
  const questions = useSelector(questionsSelectors.entity)
  const hasPaidPackage = usePaidPackage()
  const { query } = useRouter()
  const [baseEducationType, setBaseEducationType] = useState<{
    id: number
    name: string
  } | null>(null)

  const isSecondEducation = useMemo(() => {
    if (
      program?.programType?.value === EProgramTypes.master ||
      program?.programType?.value === EProgramTypes.aspirant
    ) {
      return false
    }

    return (
      query.educationType === '3' || (baseEducationType && baseEducationType.id === 3)
    )
  }, [query, baseEducationType, program])

  const { setUserActivity } = useUserActivity()

  const institutionId = useGetInstitutionsId()

  const handleActivity = async (): Promise<void> => {
    await setUserActivity({
      activityType: EActivityTypes.learning_request,
      institutionId,
      data: { infoPart: EActivityInfoPart.lead_form },
      programId: program?.id,
    })
  }

  const hasAdmissionInstruction = useMemo(
    () =>
      !!(
        program?.faculty?.admissionInstruction ||
        program?.institution?.admissionInstruction
      ),
    [program]
  )

  const showLidForm = useMemo(() => {
    if (!program) return false

    // если это МГУ, это показываем только хим факу МГУ
    if (program?.institution?.id === 1153 && program?.faculty?.id !== 51731) {
      return false
    }

    // если это Цифровой колледж Skillbox не показываем форму
    if (program?.institution?.id === 109094) {
      return false
    }

    return (
      !isCoursesCheck(program?.programType?.value) &&
      program?.programType?.value !== EProgramTypes.master &&
      program?.programType?.value !== EProgramTypes.aspirant &&
      program?.faculty?.id !== 57067 &&
      program?.institution?.id !== 5908 &&
      program?.institution?.id !== 6024 &&
      program?.faculty?.id !== 6024
    )
  }, [program])

  if (!program) return null

  const {
    name,
    institution,
    programType,
    shortDescription,
    trainingForms,
    disciplineHoursList,
    requiredExams,
    optionalExams,
    additionalExams,
    suitableForList,
    blockDescriptions,
    features,
    learningOutcomes,
    institutionPartnerCompanies,
    members,
    historyYears,
    seoLinks,
    speciality,
    feedbackStats,
    faculty,
    description,
    secondHigherExams,
    profiles,
  } = program

  const LidMagnet = (
    <>
      {(faculty?.type.value === EInstitutionType.College ||
        institution?.type.value === EInstitutionType.College) &&
        showLidForm && (
          <>
            <LidMagnetForm
              title='Хочешь сдать ОГЭ на&nbsp;5+ и&nbsp;поступить в&nbsp;этот колледж на&nbsp;бюджет?'
              text='Поможем подготовиться к&nbsp;ОГЭ на&nbsp;высокие баллы с&nbsp;гарантией поступления в&nbsp;онлайн-школе Учеба.ру'
              buttonText='Подготовиться к ОГЭ'
              size='large'
              funnel={EApplicationFunnels.program_main}
              productType={EApplicationFormProductTypes.oge}
              requestTarget={ETypeRequest.ege_lead_request}
              onSubmitForm={() => {
                analyticEvent({
                  targets: [EAnalyticEventTargets.ym, EAnalyticEventTargets.ur],
                  eventName: EAnalyticEvents.leadFormRequestProgramCollege,
                  section: EAnalyticSections.leadMagnetForm,
                })

                if (hasPaidPackage && isAuth) {
                  handleActivity()
                }
              }}
              onCheckedCode={() => {
                if (hasPaidPackage) {
                  handleActivity()
                }
              }}
            />

            <Spacing spacing='uxlarge' />
          </>
        )}

      {(faculty?.type.value === EInstitutionType.University ||
        institution?.type.value === EInstitutionType.University) &&
        showLidForm &&
        !isSecondEducation && (
          <>
            <LidMagnetForm
              title='Хочешь сдать ЕГЭ на&nbsp;80+ и&nbsp;поступить в&nbsp;этот вуз на&nbsp;бюджет?'
              text='«Поможем подготовиться к&nbsp;ЕГЭ на&nbsp;высокие баллы с&nbsp;гарантией поступления в&nbsp;онлайн-школе Учеба.ру»'
              buttonText='Подготовиться к ЕГЭ'
              size='large'
              funnel={EApplicationFunnels.program_main}
              productType={EApplicationFormProductTypes.ege}
              requestTarget={ETypeRequest.ege_lead_request}
              onSubmitForm={() => {
                analyticEvent({
                  targets: [EAnalyticEventTargets.ym, EAnalyticEventTargets.ur],
                  eventName: EAnalyticEvents.leadFormRequestProgramVuz,
                  section: EAnalyticSections.leadMagnetForm,
                })

                if (hasPaidPackage && isAuth) {
                  handleActivity()
                }
              }}
              onCheckedCode={() => {
                if (hasPaidPackage) {
                  handleActivity()
                }
              }}
            />

            <Spacing spacing='uxlarge' />
          </>
        )}
    </>
  )

  return (
    <div>
      {speciality && (
        <>
          <Text fontWeight='semibold'>
            {`Направление «${speciality.name}»${
              speciality.codeOkso ? `, код ${speciality.codeOkso}` : ''
            }`}
          </Text>

          <Spacing spacing='usmall' />
        </>
      )}

      <Heading h1={name} spacing='small' />

      {/* Чипсы в шапке */}
      <ProgramPageChips type={programType} feedbackStats={feedbackStats} />

      <Spacing spacing='xxlarge' />

      {/* Описание программы */}
      <ProgramPageAbout text={shortDescription} />

      <Spacing spacing='xxlarge' />

      {hasAdmissionInstruction ? (
        <RotationBannerAndAdmissionInstructions />
      ) : (
        <>{LidMagnet}</>
      )}

      <Spacing spacing='xxlarge' />

      {/* Проходнной балл на бюджет */}
      {historyYears && historyYears.length > 1 && (
        <>
          <PointsInfo title='Проходной балл на бюджет' list={historyYears} />

          <Spacing spacing='xxlarge' />
        </>
      )}

      {/* Варианты обчения */}
      {trainingForms && (
        <>
          <StudyOptions
            list={trainingForms}
            isCourses={isCoursesCheck(programType?.value)}
            setBaseEducationType={setBaseEducationType}
            programType={programType?.value}
          />

          <Spacing spacing='xxlarge' />
        </>
      )}

      {/* Профили обучения */}
      {profiles && (
        <>
          <ProgramIdPageProfiles text={profiles} />

          <Spacing spacing='xxlarge' />
        </>
      )}

      {/* Учебный план */}
      {disciplineHoursList?.length > 0 && (
        <>
          <Syllabus list={disciplineHoursList} />

          <Spacing spacing='xxlarge' />
        </>
      )}

      {hasAdmissionInstruction ? (
        <>{LidMagnet}</>
      ) : (
        <RotationBannerAndAdmissionInstructions />
      )}

      {/* Условия поступления */}
      {!isCoursesCheck(programType?.value) && (
        <>
          <AdmissionConditions
            requiredSubjects={requiredExams}
            optionalSubjects={optionalExams}
            additionalExams={additionalExams}
            hasOlympiad
            showButton={hasBudgetPlaces(program.trainingForms)}
            isSecondEducation={!!isSecondEducation}
            secondHigherExams={secondHigherExams}
          />

          <Spacing spacing='xxlarge' />
        </>
      )}

      {/* Дополнительные баллы к сумме ЕГЭ */}
      {(programType.value === EProgramTypes.higherBachelor ||
        programType.value === EProgramTypes.higherSpeciality) &&
        !isSecondEducation && (
          <>
            {(() => {
              if (
                faculty &&
                faculty.personalAchievements &&
                faculty.personalAchievements.length > 0
              ) {
                return (
                  <>
                    <AdditionalPoints list={faculty?.personalAchievements} />

                    <Spacing spacing='xxlarge' />
                  </>
                )
              }

              if (
                institution &&
                institution.personalAchievements &&
                institution.personalAchievements.length > 0
              ) {
                return (
                  <>
                    <AdditionalPoints list={institution.personalAchievements} />

                    <Spacing spacing='xxlarge' />
                  </>
                )
              }

              return null
            })()}
          </>
        )}

      {/* Кому подойдет программа */}
      {suitableForList?.length > 0 && (
        <>
          <ForWhomProgram suitableForList={suitableForList} />

          <Spacing spacing='xxlarge' />
        </>
      )}

      {blockDescriptions?.length > 0 && (
        <>
          {/* Программа обучения */}
          <StudyProgram blockDescriptions={blockDescriptions} />

          <Spacing spacing='xxlarge' />
        </>
      )}

      {/* Преимущества обучения */}
      {features?.length > 0 && (
        <>
          <StudyAdvantages features={features} />

          <Spacing spacing='xxlarge' />
        </>
      )}

      {description && <ExtraInfo text={description} />}

      {/* Особенности программы */}
      {/* <ProgramFeatures /> */}

      <Spacing spacing='xxlarge' />

      {/* Преподаватели */}
      {members?.length > 0 && (
        <>
          <ProgramIdPageTeachers teachers={members} />

          <Spacing spacing='xxlarge' />
        </>
      )}

      {/* Результаты обучения */}
      {learningOutcomes?.length > 0 && (
        <>
          <LearningOutcomes list={learningOutcomes} />

          <Spacing spacing='xxlarge' />
        </>
      )}

      {/* Работодатели-партнеры */}
      {institutionPartnerCompanies?.length > 0 && (
        <>
          <ProgramIdPageEmployers
            institutionPartnerCompanies={institutionPartnerCompanies}
          />

          <Spacing spacing='uxlarge' />
        </>
      )}

      {/* Отзывы */}
      {feedbacks && (
        <>
          <ProgramPageFeedbacks
            feedbacks={feedbacks.items}
            total={feedbacks.total}
            id={program.id}
            feedbackStats={feedbackStats}
          />

          <Spacing spacing='uxlarge' />
        </>
      )}

      {/* Вопросы */}
      {questions && hasPaidPackage && (
        <>
          <ProgramPageQuestions
            questions={questions.items}
            total={questions.total}
            id={program.id}
          />

          <Spacing spacing='uxlarge' />
        </>
      )}

      {institution && !hasPaidPackage && (
        <ProgramPageSimilarProgramsOthers
          type={faculty?.type || institution?.type}
          id={program.id}
          excludeId={institution?.id}
        />
      )}

      {institution && (
        <ProgramPageSimilarPrograms
          type={faculty?.type || institution?.type}
          id={program.id}
          institutionId={institution?.id}
        />
      )}

      {seoLinks && seoLinks.length && (
        <>
          <InstitutionSeeAlso links={seoLinks} />

          <Spacing spacing='uxlarge' />
        </>
      )}
    </div>
  )
}

export default ProgramPageContent
